/**
 * Deployment environment and deployment hooks (task 760).
 *
 * Report: `project/reports/innovate-service-deployment-environments.md`.
 *
 * Two concerns, both read through the app's standard service layer (never a raw
 * fetch):
 *
 *   1. The **environment list** — the Account's deployment stages, in promotion
 *      order. Every environments surface consults it first, and it is gated on
 *      the Publishing setting: environments only mean something once published
 *      versions exist, so an Account that has not adopted Publishing makes no
 *      environment calls at all.
 *   2. The **deployments** for one AI Service — what each environment serves
 *      right now, the rollout history behind it, and the one call that repoints
 *      an environment (promotion and rollback alike).
 *
 * A failed list resolves to "no environments" rather than to an error, matching
 * the versioning hooks' precedent: when the app cannot confirm that environments
 * exist, it must look exactly like an Account that has none.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deploymentsApi, environmentsApi } from '@/services/api';
import { useVersioningEnabled } from '@/hooks/useResourceVersions';
import { BARE_URL_ENVIRONMENT_PATH } from '@/types/environments';
import type {
  CreateDeploymentInput,
  CreateEnvironmentInput,
  Environment,
  UpdateEnvironmentInput,
} from '@/types/environments';

export const ENVIRONMENTS_QUERY_KEY = ['environments'] as const;

export const currentDeploymentsQueryKey = (endpointId: string | undefined) =>
  ['deployments', 'current', endpointId] as const;

export const deploymentHistoryQueryKey = (endpointId: string | undefined) =>
  ['deployments', 'history', endpointId] as const;

/** No environments — what every surface renders when the list cannot be read. */
const NO_ENVIRONMENTS: Environment[] = [];

/**
 * The Account's deployment environments, in promotion order.
 *
 * Gated on Publishing: with the practice unadopted this never calls, and answers
 * an empty list, which is exactly what an Account with no environments looks
 * like. `options.enabled` lets a caller hold the query back further (the Test
 * tab does not need it for an AI Service serving its working state).
 */
export const useEnvironments = (options?: { enabled?: boolean }) => {
  const { enabled: versioningEnabled } = useVersioningEnabled();

  const query = useQuery({
    queryKey: ENVIRONMENTS_QUERY_KEY,
    queryFn: () =>
      environmentsApi
        .getAll()
        .then((res) => res.data.data ?? NO_ENVIRONMENTS)
        .catch(() => NO_ENVIRONMENTS),
    enabled: versioningEnabled && options?.enabled !== false,
    retry: false,
  });

  return {
    ...query,
    /**
     * Always an array, in the release sequence: `order` ascending, with the
     * environment holding `/` LAST whatever its stored `order` (task 1134). The
     * service returns the list in that sequence already; this keeps it when the
     * cache holds rows the service sorted differently, and never puts `/` first
     * the way a bare `order` sort would on an Account whose `/` was minted at
     * `order` 0.
     */
    environments: [...(query.data ?? NO_ENVIRONMENTS)].sort(
      (a, b) =>
        Number(a.path === BARE_URL_ENVIRONMENT_PATH) - Number(b.path === BARE_URL_ENVIRONMENT_PATH) ||
        a.order - b.order
    ),
  };
};

export const useCreateEnvironment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEnvironmentInput) =>
      environmentsApi.create(data).then((res) => res.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ENVIRONMENTS_QUERY_KEY });
    },
  });
};

/**
 * Rename, reorder, or set the default.
 *
 * The whole list is invalidated rather than the one row patched, because two of
 * these three writes change other rows: making one environment the default
 * clears the previous default server-side, and a reorder shifts the sequence
 * everything else renders in.
 */
export const useUpdateEnvironment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEnvironmentInput }) =>
      environmentsApi.update(id, data).then((res) => res.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ENVIRONMENTS_QUERY_KEY });
    },
  });
};

export const useDeleteEnvironment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => environmentsApi.delete(id).then((res) => res.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ENVIRONMENTS_QUERY_KEY });
      // A deleted environment cannot still be serving anything.
      void queryClient.invalidateQueries({ queryKey: ['deployments'] });
    },
  });
};

/** What each environment serves for this AI Service right now. */
export const useCurrentDeployments = (
  endpointId: string | undefined,
  options?: { enabled?: boolean }
) => {
  const { enabled: versioningEnabled } = useVersioningEnabled();

  return useQuery({
    queryKey: currentDeploymentsQueryKey(endpointId),
    queryFn: () => deploymentsApi.current(endpointId!).then((res) => res.data.data ?? []),
    enabled: versioningEnabled && !!endpointId && options?.enabled !== false,
    retry: false,
  });
};

/** The rollout history, newest first — the promotion arrows are drawn from it. */
export const useDeploymentHistory = (
  endpointId: string | undefined,
  options?: { enabled?: boolean }
) => {
  const { enabled: versioningEnabled } = useVersioningEnabled();

  return useQuery({
    queryKey: deploymentHistoryQueryKey(endpointId),
    queryFn: () => deploymentsApi.history(endpointId!).then((res) => res.data.data ?? []),
    enabled: versioningEnabled && !!endpointId && options?.enabled !== false,
    retry: false,
  });
};

/**
 * Point an environment at a published version.
 *
 * This is the ONE write behind both "promote" and "roll back" — the difference
 * is only which version the caller names, so there is deliberately no second
 * hook that would let the two drift apart. Both reads are invalidated because a
 * new pointer changes what is current AND appends to the history.
 */
export const useCreateDeployment = (endpointId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDeploymentInput) =>
      deploymentsApi.create(endpointId!, data).then((res) => res.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: currentDeploymentsQueryKey(endpointId) });
      void queryClient.invalidateQueries({ queryKey: deploymentHistoryQueryKey(endpointId) });
      // AND THE ENVIRONMENT LIST (task 871). Releasing to the default `/` lane
      // on an account with no rows creates that row in the same transaction, so
      // the list this app drew the lane from is stale the instant the write
      // succeeds — and the lane it draws next must be the real one.
      void queryClient.invalidateQueries({ queryKey: ENVIRONMENTS_QUERY_KEY });
    },
  });
};
